import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider } from './context/AuthContext';

// Layout
import Layout from './components/layout/Layout';

// Global auth modals (mounted once at the root level)
import AuthModal from './components/auth/AuthModal';
import LoginRequiredModal from './components/auth/LoginRequiredModal';

// Pages
import HomePage from './pages/HomePage';
import DashboardPage from './pages/DashboardPage';
import SpaceWeatherDashboard from './pages/SpaceWeatherDashboard';
import EventsPage from './pages/EventsPage';
import EventDetailPage from './pages/EventDetailPage';
import AlertsPage from './pages/AlertsPage';
import SolarSystemPage from './pages/SolarSystemPage';
import AIChatPage from './pages/AIChatPage';
import EarthWeatherPage from './pages/EarthWeatherPage';
import SpaceCalendarPage from './pages/SpaceCalendarPage';
import SatelliteTrackerPage from './pages/SatelliteTrackerPage';
import ExoplanetPage from './pages/ExoplanetPage';
import SpaceMissionsPage from './pages/SpaceMissionsPage';
import LiveISSPage from './pages/LiveISSPage';
import NightSkyPage from './pages/NightSkyPage';
import StudyZonePage from './pages/StudyZonePage';
import SpaceNewsPage from './pages/SpaceNewsPage';
import MoonPage from './pages/MoonPage';
import TelescopeFeedPage from './pages/TelescopeFeedPage';
import ProfilePage from './pages/ProfilePage';
import StargrazingPage from './pages/StargrazingPage';
import SpaceWeatherImpactPage from './pages/SpaceWeatherImpactPage';
import AdminDashboardPage from './pages/AdminDashboardPage';

// Auth pages (standalone full-page routes)
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary:    { main: '#FFFFFF', dark: '#CCCCCC', light: '#F5F5F5' },
    secondary:  { main: '#A0A0A0', dark: '#707070', light: '#C8C8C8' },
    background: { default: '#000000', paper: '#0D0D0D' },
    error:      { main: '#FF4444' },
    warning:    { main: '#FFA500' },
    success:    { main: '#4CAF50' },
    info:       { main: '#AAAAAA' },
    text: {
      primary:   '#FFFFFF',
      secondary: '#888888',
    },
  },
});

/**
 * GlobalModals — the two auth modals are mounted once here so they're always
 * accessible from any route via the AuthContext, without re-mounting on navigation.
 */
function GlobalModals() {
  return (
    <>
      <AuthModal />
      <LoginRequiredModal />
    </>
  );
}

function App() {
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          {/* Global modals must be inside AuthProvider + BrowserRouter */}
          <GlobalModals />

          <Routes>
            {/* ── Standalone auth pages (full-screen, no sidebar) ──────── */}
            <Route path="/login"           element={<LoginPage />} />
            <Route path="/register"        element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />

            {/* ── Main app layout — fully public (guests + users) ──────── */}
            <Route path="/" element={<Layout />}>
              <Route index                           element={<HomePage />} />
              <Route path="dashboard"               element={<SpaceWeatherDashboard />} />
              <Route path="events"                  element={<EventsPage />} />
              <Route path="events/:id"              element={<EventDetailPage />} />
              <Route path="alerts"                  element={<AlertsPage />} />
              <Route path="solar-system"            element={<SolarSystemPage />} />
              <Route path="ai-chat"                 element={<AIChatPage />} />
              <Route path="earth-weather"           element={<EarthWeatherPage />} />
              <Route path="space-calendar"          element={<SpaceCalendarPage />} />
              <Route path="exoplanets"              element={<ExoplanetPage />} />
              <Route path="space-missions"          element={<SpaceMissionsPage />} />
              <Route path="iss-tracker"             element={<LiveISSPage />} />
              <Route path="night-sky"               element={<NightSkyPage />} />
              <Route path="study-zone"              element={<StudyZonePage />} />
              <Route path="space-news"              element={<SpaceNewsPage />} />
              <Route path="satellite-tracker"       element={<SatelliteTrackerPage />} />
              <Route path="moon"                    element={<MoonPage />} />
              <Route path="telescope"               element={<TelescopeFeedPage />} />
              <Route path="profile"                 element={<ProfilePage />} />
              <Route path="stargazing"              element={<StargrazingPage />} />
              <Route path="impact-analyzer"         element={<SpaceWeatherImpactPage />} />
              <Route path="admin"                   element={<AdminDashboardPage />} />
            </Route>

            {/* Catch-all → home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
